import React, { useCallback, useEffect, useState } from 'react';
import { AssessmentResultReport, AssessmentResultStatus, AssessmentResultsPage } from '../../models/assessment-results';
import { reportingService } from '../../services/reportingService';
import { formatDateToLocal } from '../../utils/dateUtils';
import styles from './AssessmentResults.module.css';

const pageSize = 25;

const statusOptions: Array<{ value: AssessmentResultStatus; label: string }> = [
  { value: 'All', label: 'All candidates' },
  { value: 'Passed', label: 'Passed' },
  { value: 'Failed', label: 'Failed' },
  { value: 'InProgress', label: 'In progress' },
  { value: 'NotStarted', label: 'Not started' },
];

const statusLabel = (status: AssessmentResultReport['status']): string => {
  switch (status) {
    case 'InProgress':
      return 'In progress';
    case 'NotStarted':
      return 'Not started';
    default:
      return status;
  }
};

const statusClass = (status: AssessmentResultReport['status']): string => {
  switch (status) {
    case 'Passed':
      return styles.passed;
    case 'Failed':
      return styles.failed;
    case 'InProgress':
      return styles.inProgress;
    default:
      return styles.notStarted;
  }
};

const formatScore = (score?: number): string =>
  score === undefined || score === null ? '—' : `${score.toFixed(1)}%`;

const formatDate = (value?: string): string =>
  value ? formatDateToLocal(value) : '—';

const AssessmentResults: React.FC = () => {
  const [report, setReport] = useState<AssessmentResultsPage | null>(null);
  const [status, setStatus] = useState<AssessmentResultStatus>('All');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadResults = useCallback(async (pageNumber: number) => {
    setLoading(true);
    setError('');

    try {
      const response = await reportingService.getAssessmentResults(
        pageNumber,
        pageSize,
        status,
        searchTerm || undefined
      );

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Assessment results could not be loaded.');
      }

      setReport(response.data);
      setCurrentPage(pageNumber);
    } catch (requestError: any) {
      const isMissingAssignment = requestError.response?.status === 404;
      setError(isMissingAssignment
        ? 'The configured CCAT assessment assignment was not found. Create or name the assignment “CCAT Assessment”, then try again.'
        : 'Assessment results could not be loaded. Please try again.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, status]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
    void loadResults(1);
  }, [loadResults]);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    setNotice('');

    try {
      const blob = await reportingService.exportAssessmentResults(
        status,
        searchTerm || undefined
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-results-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setNotice('The filtered assessment results have been exported.');
    } catch {
      setError('The assessment results could not be exported. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const resultPage = report?.results;
  const totalPages = Math.max(resultPage?.totalPages ?? 1, 1);
  const results = resultPage?.data ?? [];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerCopy}>
          <div className={styles.titleRow}>
            <span className={styles.titleIcon} aria-hidden="true">
              <img src="/images/icons/line-chart.svg" alt="" />
            </span>
            <div>
              <h2 className={styles.title}>Assessment results</h2>
              <p className={styles.subtitle}>
                Find the candidates who passed or failed CCAT, then export the exact filtered list for next steps.
              </p>
            </div>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => void loadResults(currentPage)}
            disabled={loading || exporting}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void handleExport()}
            disabled={loading || exporting || !report}
          >
            <img src="/images/icons/save.svg" alt="" />
            {exporting ? 'Preparing CSV…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage} role="alert">
          <strong>Report unavailable</strong>
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className={styles.successMessage} role="status">
          {notice}
        </div>
      )}

      {report && (
        <>
          <div className={styles.rosterNote}>
            <div>
              <span className={styles.noteLabel}>Current cohort</span>
              <strong>{report.assignmentTitle}</strong>
            </div>
            <span>New users are enrolled automatically and will appear here once they have access to the published assessment.</span>
          </div>

          <div className={styles.summaryGrid} aria-label="Assessment result totals">
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Candidates</span>
              <strong className={styles.summaryValue}>{report.totalCandidates}</strong>
              <span className={styles.summaryHint}>in this cohort</span>
            </div>
            <div className={`${styles.summaryItem} ${styles.summaryPassed}`}>
              <span className={styles.summaryLabel}>Passed</span>
              <strong className={styles.summaryValue}>{report.passedCandidates}</strong>
              <span className={styles.summaryHint}>ready for next steps</span>
            </div>
            <div className={`${styles.summaryItem} ${styles.summaryFailed}`}>
              <span className={styles.summaryLabel}>Failed</span>
              <strong className={styles.summaryValue}>{report.failedCandidates}</strong>
              <span className={styles.summaryHint}>did not meet the pass mark</span>
            </div>
            <div className={`${styles.summaryItem} ${styles.summaryPending}`}>
              <span className={styles.summaryLabel}>Pending</span>
              <strong className={styles.summaryValue}>{report.inProgressCandidates + report.notStartedCandidates}</strong>
              <span className={styles.summaryHint}>in progress or not started</span>
            </div>
          </div>
        </>
      )}

      <div className={styles.filterBar}>
        <label className={styles.searchField}>
          <span>Search candidates</span>
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Name, email, or student ID"
          />
        </label>
        <label className={styles.statusField}>
          <span>Result filter</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as AssessmentResultStatus)}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <div className={styles.filterSummary}>
          {status === 'All' ? 'Showing every candidate' : `Showing ${statusLabel(status as AssessmentResultReport['status'])} candidates`}
        </div>
      </div>

      {loading && !report ? (
        <div className={styles.loadingState} aria-label="Loading assessment results">
          <div className={styles.loadingLine} />
          <div className={styles.loadingLine} />
          <div className={styles.loadingLine} />
          <div className={styles.loadingLine} />
        </div>
      ) : results.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon} aria-hidden="true">✓</div>
          <h3>{error ? 'No result data to display' : 'No candidates match these filters'}</h3>
          <p>{error
            ? 'Once the CCAT assignment is available, its roster and results will appear here.'
            : 'Try a different result filter or search term.'}</p>
        </div>
      ) : (
        <>
          <div className={styles.tableShell}>
            <div className={styles.tableScroller}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Contact</th>
                    <th>Result</th>
                    <th>Score</th>
                    <th>Questions</th>
                    <th>Time</th>
                    <th>Finished</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result) => (
                    <tr key={`${result.assignmentId}-${result.examTakerId}`}>
                      <td>
                        <div className={styles.candidateCell}>
                          <strong>{result.fullName}</strong>
                          <span>{result.examTakerId}</span>
                        </div>
                      </td>
                      <td>
                        <span className={styles.email}>{result.email || 'No email recorded'}</span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${statusClass(result.status)}`}>
                          {statusLabel(result.status)}
                        </span>
                      </td>
                      <td>
                        <div className={styles.scoreCell}>
                          <strong>{formatScore(result.scorePercentage)}</strong>
                          <span>{result.passingScorePercentage !== undefined ? `Pass mark ${formatScore(result.passingScorePercentage)}` : 'Pass mark —'}</span>
                        </div>
                      </td>
                      <td>{result.answeredQuestions}/{result.totalQuestions}</td>
                      <td>{result.timeSpentMinutes > 0 ? `${result.timeSpentMinutes} min` : '—'}</td>
                      <td>{formatDate(result.finishedAtUtc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.pagination}>
            <span>{resultPage?.totalCount ?? 0} result{resultPage?.totalCount === 1 ? '' : 's'}</span>
            <div className={styles.paginationControls}>
              <button
                type="button"
                onClick={() => void loadResults(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
              >
                Previous
              </button>
              <strong>Page {currentPage} of {totalPages}</strong>
              <button
                type="button"
                onClick={() => void loadResults(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentResults;
